import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  MenuItem,
} from '@mui/material';
import { PlusIcon } from 'lucide-react';

import { RaLabel } from '@modules/_shared/components/form/RaLabel';
import {
  type RaSelectOption,
  SelectField,
} from '@modules/_shared/components/form/RaSelect';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import {
  PhoneInput,
  formatPhoneNumber,
} from '@modules/_shared/components/PhoneInput';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import {
  CLEAR_VISIBILITY_PHONES,
  INSERT_AND_SET_VISIBILITY_PHONE,
  SET_VISIBILITY_PHONE,
} from '@modules/_shared/components/visibility-drawer/queries/visibilityPhoneMutations';
import {
  type AgentEntity,
  resolveTeamPhone,
} from '@modules/_shared/components/visibility-drawer/types';
import { useDrawerContext } from '@modules/_shared/components/visibility-drawer/VisibilityDrawer';
import { formatValidPhoneNumberE164 } from '@modules/_shared/utils/phone';
import { useLocale } from '@shared/hooks/locale';

type AgentPhoneEditModalProps = {
  open: boolean;
  onClose: () => void;
  entity: AgentEntity;
};

const AGENCY_SENTINEL = 'agency';

const renderOption = (option: RaSelectOption) => (
  <MenuItem
    key={option.value}
    value={option.value ?? ''}
    disabled={option.disabled ?? false}
  >
    {option.label}
  </MenuItem>
);

const AgentPhoneEditModal = ({
  open,
  onClose,
  entity,
}: AgentPhoneEditModalProps) => {
  const { t } = useLocale();
  const { contextId } = useDrawerContext();
  const [apolloError, setErrorLike] = useState<ErrorLike | null>(null);
  const [showAddPhone, setShowAddPhone] = useState(false);
  const [newPhoneValue, setNewPhoneValue] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [selectedPhoneId, setSelectedPhoneId] = useState('');

  const teamPhone = resolveTeamPhone(entity, contextId);

  const userPhoneNumbers = useMemo(
    () => entity.phone_numbers ?? [],
    [entity.phone_numbers],
  );

  useEffect(() => {
    if (open) {
      const visibilityPhone = userPhoneNumbers.find(pn => pn.is_visibility);
      setSelectedPhoneId(
        visibilityPhone ? visibilityPhone.id : AGENCY_SENTINEL,
      );
      setShowAddPhone(false);
      setNewPhoneValue('');
      setPhoneError(null);
    }
    // Only reset when modal opens or entity changes, not when phone list updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, entity.id]);

  const [clearVisibility, { loading: clearLoading }] = useMutation(
    CLEAR_VISIBILITY_PHONES,
    { onError: setErrorLike },
  );

  const [setVisibility, { loading: setVisibilityLoading }] = useMutation(
    SET_VISIBILITY_PHONE,
    { onError: setErrorLike },
  );

  const [insertAndSetVisibility, { loading: insertLoading }] = useMutation(
    INSERT_AND_SET_VISIBILITY_PHONE,
    {
      refetchQueries: ['GetUser'],
      awaitRefetchQueries: true,
      onError: error => {
        const isUniqueness = error.message.includes('Uniqueness violation');
        if (isUniqueness) {
          setPhoneError(t('This phone number already exists'));
        } else {
          setErrorLike(error);
        }
      },
    },
  );

  const loading = clearLoading || setVisibilityLoading || insertLoading;

  const phoneOptions: RaSelectOption[] = useMemo(() => {
    const agencyOption: RaSelectOption = teamPhone
      ? {
          value: AGENCY_SENTINEL,
          label: `${formatPhoneNumber(teamPhone.number)} (${t('Agency')})`,
        }
      : {
          value: AGENCY_SENTINEL,
          label: t('No agency phone number'),
          disabled: true,
        };

    return [
      agencyOption,
      ...userPhoneNumbers.map(phone => ({
        value: phone.id,
        label: `${formatPhoneNumber(phone.number)}${
          phone.primary ? ` (${t('Primary')})` : ''
        }`,
      })),
    ];
  }, [userPhoneNumbers, teamPhone, t]);

  const handleSelectChange = useCallback((e: { target: { value: string } }) => {
    setSelectedPhoneId(e.target.value);
  }, []);

  const handleAddPhone = useCallback(async () => {
    if (!newPhoneValue) {
      return;
    }

    const formattedNumber = formatValidPhoneNumberE164(newPhoneValue);
    if (formattedNumber == null) {
      setPhoneError(t('Invalid phone number'));
      return;
    }
    setPhoneError(null);

    const existingPhone = (entity.phone_numbers ?? []).find(
      pn => pn.number === formattedNumber,
    );

    if (existingPhone) {
      setPhoneError(t('This phone number already exists'));
      return;
    }

    const result = await insertAndSetVisibility({
      variables: {
        userId: entity.id,
        input: {
          user_id: entity.id,
          number: formattedNumber,
          primary: false,
          is_visibility: true,
        },
      },
    });

    if (result.data) {
      onClose();
    }
  }, [
    newPhoneValue,
    entity.id,
    entity.phone_numbers,
    insertAndSetVisibility,
    t,
    onClose,
  ]);

  const onSave = useCallback(async () => {
    if (showAddPhone && newPhoneValue) {
      return handleAddPhone();
    }

    let result;
    if (selectedPhoneId === AGENCY_SENTINEL) {
      result = await clearVisibility({ variables: { userId: entity.id } });
    } else {
      result = await setVisibility({
        variables: { userId: entity.id, phoneId: selectedPhoneId },
      });
    }

    if (result.data) {
      onClose();
    }
  }, [
    showAddPhone,
    newPhoneValue,
    handleAddPhone,
    selectedPhoneId,
    entity.id,
    clearVisibility,
    setVisibility,
    onClose,
  ]);

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={t('Phone number')}
      onSave={onSave}
      loading={loading}
    >
      <Box sx={{ p: 2 }}>
        <FormControl fullWidth>
          <RaLabel label={t('Visibility phone number')} />
          <SelectField
            field={{
              value: selectedPhoneId,
              onChange: handleSelectChange,
              onBlur: () => {},
              name: 'phoneId',
              ref: () => {},
            }}
            options={phoneOptions}
            renderOption={renderOption}
          />
        </FormControl>

        {!showAddPhone && (
          <Button
            startIcon={<PlusIcon />}
            onClick={() => setShowAddPhone(true)}
            sx={{ mt: 1 }}
          >
            {t('Add new number')}
          </Button>
        )}

        {showAddPhone && (
          <FormControl fullWidth sx={{ mt: 2 }}>
            <RaLabel label={t('New phone number')} />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <PhoneInput
                value={newPhoneValue}
                onChange={(value: string) => {
                  setNewPhoneValue(value);
                  if (phoneError) {
                    setPhoneError(null);
                  }
                }}
                size="small"
                variant="outlined"
                slotProps={{
                  input: {
                    sx: { background: 'var(--mui-palette-background-paper)' },
                  },
                }}
                fullWidth
                error={phoneError != null}
              />
              <Button
                variant="contained"
                onClick={handleAddPhone}
                disabled={!newPhoneValue || insertLoading}
                sx={{ mt: 0.15, whiteSpace: 'nowrap' }}
              >
                {t('Add')}
              </Button>
            </Box>
            {phoneError && <FormHelperText error>{phoneError}</FormHelperText>}
          </FormControl>
        )}
      </Box>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setErrorLike(null)}
      />
    </SlideDialog>
  );
};

export default AgentPhoneEditModal;
